import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Stack, Typography, IconButton, CircularProgress } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiTextArea from 'features/ui/base-components/pai-text-area';
import CloseIcon from '@mui/icons-material/Close';
import { ApprovedSubregion, ApprovedSubsearch, ApprovedPeopleSearch } from 'services/lead-list-research.service';

interface PromptApprovalDialogProps {
  open: boolean;
  onClose: () => void;
  onStart: () => void;
  onBack: () => void;
  approvedSubregions: ApprovedSubregion[];
  approvedSubsearches: ApprovedSubsearch[];
  approvedPeopleSearches: ApprovedPeopleSearch[];
  onChangeSubregion: (idx: number, value: Partial<ApprovedSubregion>) => void;
  onChangeSubsearch: (idx: number, value: Partial<ApprovedSubsearch>) => void;
  onChangePeopleSearch: (idx: number, value: Partial<ApprovedPeopleSearch>) => void;
  onAddSubregion: () => void;
  onAddSubsearch: () => void;
  onAddPeopleSearch: () => void;
  onRemoveSubregion: (idx: number) => void;
  onRemoveSubsearch: (idx: number) => void;
  onRemovePeopleSearch: (idx: number) => void;
  loading?: boolean;
}

const PromptApprovalDialog: React.FC<PromptApprovalDialogProps> = ({
  open,
  onClose,
  onStart,
  onBack,
  approvedSubregions,
  approvedSubsearches,
  approvedPeopleSearches,
  onChangeSubregion,
  onChangeSubsearch,
  onChangePeopleSearch,
  onAddSubregion,
  onAddSubsearch,
  onAddPeopleSearch,
  onRemoveSubregion,
  onRemoveSubsearch,
  onRemovePeopleSearch,
  loading,
}) => {
  const allFilled =
    approvedSubregions.every((p) => p.name.trim() && p.description.trim()) &&
    approvedSubsearches.every((p) => p.name.trim() && p.instruction.trim()) &&
    approvedPeopleSearches.every((p) => p.name.trim() && p.instruction.trim());

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Review & Approve Prompts</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle1" gutterBottom>
          Subregions
        </Typography>
        <Stack gap={2} mb={3}>
          {approvedSubregions.map((subregion, idx) => (
            <Stack key={idx} direction="row" alignItems="flex-start" gap={1}>
              <PaiTextArea
                value={subregion.name}
                onChange={(e) => onChangeSubregion(idx, { name: e.target.value })}
                rows={1}
                placeholder="Subregion name"
              />
              <PaiTextArea
                value={subregion.description}
                onChange={(e) => onChangeSubregion(idx, { description: e.target.value })}
                rows={1}
                placeholder="Description"
              />
              <IconButton onClick={() => onRemoveSubregion(idx)} size="small" title="Remove">
                <CloseIcon />
              </IconButton>
            </Stack>
          ))}
          <PaiButton text="Add Subregion" action={onAddSubregion} size="small" border="rounded" />
        </Stack>
        <Typography variant="subtitle1" gutterBottom>
          Subsearches
        </Typography>
        <Stack gap={2} mb={3}>
          {approvedSubsearches.map((subsearch, idx) => (
            <Stack key={idx} direction="row" alignItems="flex-start" gap={1}>
              <PaiTextArea
                value={subsearch.name}
                onChange={(e) => onChangeSubsearch(idx, { name: e.target.value })}
                rows={1}
                placeholder="Subsearch name"
              />
              <PaiTextArea
                value={subsearch.instruction}
                onChange={(e) => onChangeSubsearch(idx, { instruction: e.target.value })}
                rows={1}
                placeholder="Instruction"
              />
              <IconButton onClick={() => onRemoveSubsearch(idx)} size="small" title="Remove">
                <CloseIcon />
              </IconButton>
            </Stack>
          ))}
          <PaiButton text="Add Subsearch" action={onAddSubsearch} size="small" border="rounded" />
        </Stack>
        <Typography variant="subtitle1" gutterBottom>
          People Searches
        </Typography>
        <Stack gap={2}>
          {approvedPeopleSearches.map((people, idx) => (
            <Stack key={idx} direction="row" alignItems="flex-start" gap={1}>
              <PaiTextArea
                value={people.name}
                onChange={(e) => onChangePeopleSearch(idx, { name: e.target.value })}
                rows={1}
                placeholder="Role name"
              />
              <PaiTextArea
                value={people.instruction}
                onChange={(e) => onChangePeopleSearch(idx, { instruction: e.target.value })}
                rows={1}
                placeholder="Instruction"
              />
              <IconButton onClick={() => onRemovePeopleSearch(idx)} size="small" title="Remove">
                <CloseIcon />
              </IconButton>
            </Stack>
          ))}
          <PaiButton text="Add People Search" action={onAddPeopleSearch} size="small" border="rounded" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <PaiButton text="Back" action={onBack} size="small" border="rounded" />
        <PaiButton text="Cancel" action={onClose} size="small" border="rounded" />
        <PaiButton
          text={loading ? '' : 'Start Full Research'}
          action={onStart}
          size="small"
          border="rounded"
          disabled={!allFilled || loading}
          startIcon={loading ? <CircularProgress size={18} /> : undefined}
        />
      </DialogActions>
    </Dialog>
  );
};

export default PromptApprovalDialog;